import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ThumbsUp, ChatCircle, ShareFat, Heart, PaperPlaneTilt, BookmarkSimple, GlobeSimple, DotsThree, CaretRight } from "@phosphor-icons/react";
import { API } from "@/lib/api";

const CTA_LABELS = {
  LEARN_MORE: "Learn more", SIGN_UP: "Sign up", APPLY_NOW: "Apply now",
  GET_QUOTE: "Get quote", CONTACT_US: "Contact us", SUBSCRIBE: "Subscribe", DOWNLOAD: "Download",
};

const truncate = (t, n) => (t && t.length > n ? t.slice(0, n).trimEnd() : null);

const Avatar = ({ name, size = 40 }) => (
  <span style={{ height: size, width: size }}
    className="rounded-full bg-[#0A0D14] text-[#FFD400] font-heading font-extrabold flex items-center justify-center text-xs shrink-0">
    {(name || "TB").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
  </span>
);

function FacebookPreview({ job, pageName }) {
  const pkg = job.ad_package;
  const short = truncate(pkg.primary_text, 125);
  return (
    <div className="w-full max-w-[380px] mx-auto bg-white rounded-xl border border-[#dcdfe4] shadow-sm overflow-hidden font-[Helvetica,Arial,sans-serif]" data-testid="fb-preview-card">
      <div className="flex items-center gap-2.5 px-3 pt-3">
        <Avatar name={pageName} />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-bold text-[#050505] leading-tight">{pageName}</div>
          <div className="flex items-center gap-1 text-[12px] text-[#65676B]">Sponsored · <GlobeSimple size={11} /></div>
        </div>
        <DotsThree size={22} className="text-[#65676B]" />
      </div>
      <div className="px-3 py-2 text-[14px] text-[#050505] leading-snug">
        {short ? <>{short}… <span className="text-[#65676B]">See more</span></> : pkg.primary_text}
      </div>
      <img src={`${API}/team/jobs/${job.id}/image?v=${encodeURIComponent(job.updated_at || "")}`} alt={pkg.headline} className="w-full aspect-square object-cover" />
      <div className="flex items-center gap-3 bg-[#F0F2F5] px-3 py-2.5">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase text-[#65676B] tracking-wide">Form</div>
          <div className="text-[14px] font-bold text-[#050505] leading-tight truncate">{pkg.headline}</div>
        </div>
        <button className="shrink-0 bg-[#E4E6EB] hover:bg-[#d8dadf] text-[#050505] text-[14px] font-semibold rounded-md px-3.5 py-1.5">
          {CTA_LABELS[pkg.cta] || "Learn more"}
        </button>
      </div>
      <div className="border-t border-[#dcdfe4] mx-3" />
      <div className="flex items-center justify-around py-1.5 text-[#65676B]">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold py-1"><ThumbsUp size={17} /> Like</span>
        <span className="flex items-center gap-1.5 text-[13px] font-semibold py-1"><ChatCircle size={17} /> Comment</span>
        <span className="flex items-center gap-1.5 text-[13px] font-semibold py-1"><ShareFat size={17} /> Share</span>
      </div>
    </div>
  );
}

function InstagramPreview({ job, pageName }) {
  const pkg = job.ad_package;
  const handle = (pageName || "trainingbasket").toLowerCase().replace(/\s+/g, ".");
  const short = truncate(pkg.primary_text, 90);
  return (
    <div className="w-full max-w-[380px] mx-auto bg-white rounded-xl border border-[#dcdfe4] shadow-sm overflow-hidden font-[Helvetica,Arial,sans-serif]" data-testid="ig-preview-card">
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <Avatar name={pageName} size={32} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-[#050505] leading-tight">{handle}</div>
          <div className="text-[11px] text-[#65676B]">Sponsored</div>
        </div>
        <DotsThree size={20} className="text-[#050505]" />
      </div>
      <img src={`${API}/team/jobs/${job.id}/image?v=${encodeURIComponent(job.updated_at || "")}`} alt={pkg.headline} className="w-full aspect-square object-cover" />
      <button className="w-full flex items-center justify-between px-3 py-2.5 text-[14px] font-semibold text-[#0095F6] border-b border-[#efefef]">
        {CTA_LABELS[pkg.cta] || "Learn more"} <CaretRight size={14} weight="bold" />
      </button>
      <div className="flex items-center gap-4 px-3 py-2.5 text-[#050505]">
        <Heart size={22} /> <ChatCircle size={22} /> <PaperPlaneTilt size={22} />
        <BookmarkSimple size={22} className="ml-auto" />
      </div>
      <div className="px-3 pb-3 text-[13px] text-[#050505] leading-snug">
        <b>{handle}</b> {short ? <>{short}… <span className="text-[#65676B]">more</span></> : pkg.primary_text}
      </div>
    </div>
  );
}

export default function AdPreview({ job, pageName, onClose }) {
  if (!job?.ad_package) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0 rounded-xl overflow-hidden" data-testid="ad-preview-dialog">
        <div className="px-5 pt-4 pb-3 border-b border-[#E5E7EB]">
          <DialogTitle className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">Ad preview</DialogTitle>
          <p className="text-[11px] text-[#8a90a0] mt-0.5">Exactly how it renders in the feed — like Ads Manager</p>
        </div>
        <Tabs defaultValue="facebook" className="p-4">
          <TabsList className="bg-[#F3F4F6] rounded-lg p-1 w-full">
            <TabsTrigger value="facebook" data-testid="preview-tab-facebook" className="flex-1 rounded-md text-xs font-bold">Facebook Feed</TabsTrigger>
            <TabsTrigger value="instagram" data-testid="preview-tab-instagram" className="flex-1 rounded-md text-xs font-bold">Instagram Feed</TabsTrigger>
          </TabsList>
          <div className="mt-4 max-h-[65vh] overflow-y-auto bg-[#F0F2F5] rounded-lg p-4">
            <TabsContent value="facebook" className="mt-0"><FacebookPreview job={job} pageName={pageName} /></TabsContent>
            <TabsContent value="instagram" className="mt-0"><InstagramPreview job={job} pageName={pageName} /></TabsContent>
          </div>
          <p className="text-[10px] text-[#8a90a0] mt-3">Ad rendering and interaction may vary based on device, format and other factors.</p>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
